import * as api from '@gitbook/api-client';
import { Alert, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';

import { AdsRequestReviewDialog } from './AdsRequestReviewModal';
import {
    type AdsReviewStatus,
    AdsStatusIndicator,
    isSiteAdsInReviewStatus,
    useAdsReviewStatus,
} from './AdsStatus';

interface AdsReviewStatusAlertProps {
    organizationId: string;
    site: api.Site;
}

/**
 * Alert displayed on the home of a sponsored site to show its Ads review status.
 */
export function AdsReviewStatusAlert(props: AdsReviewStatusAlertProps) {
    const { site } = props;

    if (site.type !== api.SiteType.Sponsored || !site.ads || !isSiteAdsInReviewStatus(site.ads)) {
        return null;
    }

    // Pending is now the default status for sponsored sites, no need to show the alert for it
    if (site.ads.status === api.SiteAdsStatus.Pending) {
        return null;
    }

    return <EffectiveAdsReviewStatusAlert {...props} ads={site.ads} />;
}

function EffectiveAdsReviewStatusAlert(
    props: AdsReviewStatusAlertProps & { ads: AdsReviewStatus }
) {
    const { site, organizationId } = props;
    const reviewStatusProps = useAdsReviewStatus(props);
    const dialogTrigger = useDialogTriggerState();

    return (
        <Alert
            action={
                reviewStatusProps.action
                    ? { ...reviewStatusProps.action, onPress: () => dialogTrigger.open() }
                    : undefined
            }
            details={
                <DialogRoot {...dialogTrigger.rootProps}>
                    <AdsStatusIndicator badge={reviewStatusProps.badge}>
                        {reviewStatusProps.details}
                    </AdsStatusIndicator>
                    <AdsRequestReviewDialog organizationId={organizationId} site={site} />
                </DialogRoot>
            }
        />
    );
}
